import React, { useState, useEffect, useRef } from "react";
import { FaStar, FaQuoteLeft } from "react-icons/fa";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const testimonials = [
  {
    type: "restaurant",
    name: "Rakesh Sharma",
    role: "Restaurant Owner",
    city: "Pune",
    comment: "Zafflo completely changed how we take orders. Customers scan the QR and orders come instantly on WhatsApp — no confusion, no missed items.",
    stat: "Orders up 40%",
    initials: "RS",
    gradient: ["#fde68a","#fcd34d"],
    textColor: "#92400e",
  },
  {
    type: "restaurant",
    name: "Mohammed Irfan",
    role: "Café Founder",
    city: "Bengaluru",
    comment: "Clean interface, fast support, and no unnecessary features. Perfect for small restaurants who want to move fast without the overhead.",
    stat: "Setup in 8 min",
    initials: "MI",
    gradient: ["#bfdbfe","#93c5fd"],
    textColor: "#1e40af",
  },
  {
    type: "hotel",
    name: "Anjali Verma",
    role: "Hotel Manager",
    city: "Jaipur",
    comment: "Room service ordering became seamless overnight. Guests scan from the bedside card, order, and we process it instantly — no phone tag.",
    stat: "0 missed orders",
    initials: "AV",
    gradient: ["#d9f99d","#bbf7d0"],
    textColor: "#14532d",
  },
  {
    type: "salon",
    name: "Neha Kapoor",
    role: "Salon Owner",
    city: "Delhi",
    comment: "Clients book services directly from the QR. Zafflo cut our phone calls by half and eliminated double-bookings completely.",
    stat: "Calls cut by 50%",
    initials: "NK",
    gradient: ["#fecdd3","#fda4af"],
    textColor: "#9f1239",
  },
  {
    type: "spa",
    name: "Rahul Mehta",
    role: "Spa Manager",
    city: "Goa",
    comment: "Guests book treatments directly from the QR menu. The experience feels modern and premium — it matches the vibe of our property perfectly.",
    stat: "Bookings +65%",
    initials: "RM",
    gradient: ["#e9d5ff","#d8b4fe"],
    textColor: "#5b21b6",
  },
  {
    type: "homechef",
    name: "Pooja Desai",
    role: "Home Chef",
    city: "Mumbai",
    comment: "I share my QR menu on WhatsApp Stories and customers order instantly. It simplified my entire food business — I run everything solo now.",
    stat: "Solo operation",
    initials: "PD",
    gradient: ["#fed7aa","#fdba74"],
    textColor: "#9a3412",
  },
];

const categories = [
  { key: "restaurant", label: "Restaurants", emoji: "🍽️" },
  { key: "hotel",      label: "Hotels",      emoji: "🏨" },
  { key: "salon",      label: "Salons",      emoji: "💇" },
  { key: "spa",        label: "Spas",        emoji: "💆" },
  { key: "homechef",   label: "Home Chefs",  emoji: "👩‍🍳" },
];

const stats = [
  { value: "50+",  label: "Businesses live",    sub: "and growing" },
  { value: "10K+", label: "Orders processed",   sub: "across India" },
  { value: "5.0★", label: "Average rating",     sub: "from real users" },
];

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useWindowWidth() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTimeout(() => setVis(true), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, vis };
}

/* ─────────────────────────────────────────────
   TOKENS & HELPERS
───────────────────────────────────────────── */
const G = "#6cbb23";
const T = {
  zh: { fontFamily: "'Fraunces',Georgia,serif" },
  zb: { fontFamily: "'Plus Jakarta Sans',sans-serif" },
};

const revealStyle = (vis: boolean, delay = 0): React.CSSProperties => ({
  opacity: vis ? 1 : 0,
  transform: vis ? "translateY(0)" : "translateY(22px)",
  transition: `opacity .58s ${delay}ms cubic-bezier(.22,.68,0,1.2), transform .58s ${delay}ms cubic-bezier(.22,.68,0,1.2)`,
});

const cardReveal = (vis: boolean, delay = 0): React.CSSProperties => ({
  opacity: vis ? 1 : 0,
  transform: vis ? "translateY(0) scale(1)" : "translateY(18px) scale(.97)",
  transition: `opacity .5s ${delay}ms cubic-bezier(.22,.68,0,1.2), transform .5s ${delay}ms cubic-bezier(.22,.68,0,1.2)`,
});

/* ─────────────────────────────────────────────
   FONT IMPORT + MINIMAL ANIMATIONS
───────────────────────────────────────────── */
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    .sp-cat-btn {
      transition: background .2s, color .2s, border-color .2s, transform .15s;
      cursor: pointer;
    }
    .sp-cat-btn:hover { transform: translateY(-1px); }

    .sp-tcard {
      transition: transform .22s, box-shadow .22s, border-color .22s;
    }
    .sp-tcard:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 56px rgba(108,187,35,.12) !important;
      border-color: #6cbb23 !important;
    }

    .sp-stat-card {
      transition: transform .2s, border-color .2s;
    }
    .sp-stat-card:hover {
      transform: translateY(-2px);
      border-color: #6cbb23 !important;
    }

    .sp-pill-hover { transition: border-color .18s; }
    .sp-pill-hover:hover { border-color: #6cbb23 !important; }

    @keyframes sp-fadein {
      from { opacity:0; transform: translateY(14px) scale(.97); }
      to   { opacity:1; transform: translateY(0)    scale(1);   }
    }
    .sp-card-enter { animation: sp-fadein .42s cubic-bezier(.22,.68,0,1.2) both; }
  `}</style>
);

/* ─────────────────────────────────────────────
   TESTIMONIAL CARD
───────────────────────────────────────────── */
const TestiCard = ({
  t, delay, isMobile,
}: {
  t: typeof testimonials[0];
  delay: number;
  isMobile: boolean;
}) => (
  <div
    className="sp-tcard sp-card-enter"
    style={{
      animationDelay: `${delay}ms`,
      background: "#fff",
      border: "1.5px solid #efefea",
      borderRadius: 20,
      padding: 28,
      display: "flex",
      flexDirection: "column",
      gap: 0,
      boxShadow: "0 4px 20px rgba(0,0,0,.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Ghost quote mark */}
    <div style={{
      position: "absolute", top: 16, right: 18,
      ...T.zh, fontSize: 90, fontWeight: 900, fontStyle: "italic",
      color: G, opacity: .045, lineHeight: 1,
      userSelect: "none", pointerEvents: "none",
    }}>"</div>

    {/* Stars */}
    <div style={{ display: "flex", gap: 3, marginBottom: 16 }}>
      {[1,2,3,4,5].map(i => (
        <svg key={i} width="13" height="13" viewBox="0 0 14 14" fill="none">
          <path d="M7 1.5L8.5 4.8l3.5.5-2.5 2.4.6 3.4L7 9.4l-3.1 1.7.6-3.4L2 5.3l3.5-.5L7 1.5z"
            fill={G} stroke={G} strokeWidth=".4"/>
        </svg>
      ))}
    </div>

    {/* Quote */}
    <p style={{
      ...T.zb, fontSize: 14, color: "#555", lineHeight: 1.8,
      fontWeight: 300, flex: 1, marginBottom: 20,
    }}>
      "{t.comment}"
    </p>

    {/* Divider */}
    <div style={{ height: 1, background: "#f0f0ec", marginBottom: 16 }} />

    {/* Footer: avatar + name + stat badge */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {/* Avatar */}
        <div style={{
          width: 38, height: 38, borderRadius: "50%",
          background: `linear-gradient(135deg, ${t.gradient[0]}, ${t.gradient[1]})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, ...T.zb, fontSize: 12, fontWeight: 700, color: t.textColor,
        }}>
          {t.initials}
        </div>
        <div>
          <div style={{ ...T.zb, fontSize: 13, fontWeight: 600, color: "#111" }}>{t.name}</div>
          <div style={{ ...T.zb, fontSize: 11, color: "#aaa" }}>{t.role} · {t.city}</div>
        </div>
      </div>

      {/* Stat badge */}
      <div style={{
        ...T.zb, fontSize: 10, fontWeight: 700, color: "#3b6d11",
        background: "#f0f9e6", border: "1.5px solid #c8e89c",
        borderRadius: 999, padding: "4px 10px", whiteSpace: "nowrap" as const,
        flexShrink: 0,
      }}>
        {t.stat}
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const SocialProof = () => {
  const width     = useWindowWidth();
  const isMobile  = width < 640;
  const isTablet  = width >= 640 && width < 960;
  const isDesktop = width >= 960;

  const [activeType, setActiveType] = useState("restaurant");
  const [renderKey, setRenderKey]   = useState(0);

  const filtered = testimonials.filter(t => t.type === activeType);

  const hdr   = useReveal(0);
  const stats_ = useReveal(80);
  const filter_ = useReveal(100);
  const grid_  = useReveal(60);

  const handleCategory = (key: string) => {
    setActiveType(key);
    setRenderKey(k => k + 1); // remount cards to re-trigger enter animation
  };

  // Grid layout: 2 col on tablet+, 1 col on mobile
  const cols = isMobile ? 1 : filtered.length === 1 ? 1 : 2;

  return (
    <section style={{
      background: "#fafaf8", position: "relative",
      overflow: "hidden", padding: "60px 0 42px",
      fontFamily: "'Plus Jakarta Sans',sans-serif",
    }}>
      <FontLink />

      {/* Grid texture */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(#efefea 1px,transparent 1px),linear-gradient(90deg,#efefea 1px,transparent 1px)",
        backgroundSize: "48px 48px", opacity: .5,
      }}/>

      {/* Top glow */}
      <div style={{
        position: "absolute", top: -100, left: "50%", transform: "translateX(-50%)",
        width: 600, height: 400, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(108,187,35,.07) 0%,transparent 70%)",
        pointerEvents: "none",
      }}/>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>

        {/* ── HEADER ── */}
        <div ref={hdr.ref} style={{ ...revealStyle(hdr.vis), textAlign: "center", marginBottom: 52 }}>
         
          <h2 style={{
            ...T.zh, fontSize: "clamp(30px,3.8vw,50px)", fontWeight: 900,
            fontStyle: "italic", color: "#111", letterSpacing: "-0.03em",
            lineHeight: 1.27, marginBottom: 16,
          }}>
            Trusted by businesses{" "}
            <mark style={{
              background: G, color: "#fff",
              padding: "1px 12px 5px", borderRadius: 6,
              fontStyle: "italic", display: "inline-block",
            }}>
              across India
            </mark>
          </h2>
          <p style={{
            ...T.zb, fontSize: 15, color: "#888", fontWeight: 300,
            maxWidth: 420, margin: "0 auto", lineHeight: 1.75,
          }}>
            Restaurants, hotels, salons, spas, and home chefs rely on Zafflo to simplify
            orders and grow repeat revenue.
          </p>
        </div>

        {/* ── STATS ROW ── */}
        <div ref={stats_.ref} style={{
          ...revealStyle(stats_.vis),
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)",
          gap: 14, marginBottom: 48,
        }}>
          {stats.map(({ value, label, sub }, i) => (
            <div
              key={label}
              className="sp-stat-card"
              style={{
                background: "#fff", border: "1.5px solid #efefea", borderRadius: 18,
                padding: "24px 20px", textAlign: "center",
                boxShadow: "0 2px 10px rgba(0,0,0,.04)",
                ...revealStyle(stats_.vis, i * 80),
              }}
            >
              <div style={{
                ...T.zh, fontSize: "clamp(34px,4vw,48px)", fontWeight: 900,
                fontStyle: "italic", color: G, lineHeight: 1, marginBottom: 6,
              }}>
                {value}
              </div>
              <div style={{ ...T.zb, fontSize: 14, fontWeight: 600, color: "#333", marginBottom: 2 }}>
                {label}
              </div>
              <div style={{ ...T.zb, fontSize: 11, color: "#bbb" }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* ── CATEGORY FILTER ── */}
        <div ref={filter_.ref} style={{
          ...revealStyle(filter_.vis),
          display: "flex", justifyContent: "center",
          marginBottom: 40,
        }}>
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center",
            background: "#fff", border: "1.5px solid #efefea",
            borderRadius: 999, padding: "5px 6px",
            boxShadow: "0 2px 12px rgba(0,0,0,.05)",
          }}>
            {categories.map(c => {
              const active = activeType === c.key;
              return (
                <button
                  key={c.key}
                  className="sp-cat-btn"
                  onClick={() => handleCategory(c.key)}
                  style={{
                    ...T.zb,
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "8px 18px", borderRadius: 999, border: "none",
                    fontSize: 13, fontWeight: 600,
                    background: active ? G : "transparent",
                    color: active ? "#fff" : "#888",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 14 }}>{c.emoji}</span>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── TESTIMONIAL GRID ── */}
        <div ref={grid_.ref}>
          <div
            key={renderKey}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gap: isMobile ? 10 : 14,
            }}
          >
            {filtered.map((t, i) => (
              <TestiCard
                key={`${t.name}-${renderKey}`}
                t={t}
                delay={i * 90}
                isMobile={isMobile}
              />
            ))}
          </div>

          {/* Category count label */}
          <div style={{
            marginTop: 18, textAlign: "center",
            ...T.zb, fontSize: 12, color: "#ccc",
          }}>
            Showing {filtered.length} review{filtered.length > 1 ? "s" : ""} · {categories.find(c => c.key === activeType)?.label}
          </div>
        </div>

        {/* ── MARQUEE LOGOS STRIP ── */}
        <div style={{ margin: "52px 0 0", overflow: "hidden", position: "relative" }}>
          {/* Fade masks */}
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0, width: 80, zIndex: 2,
            background: "linear-gradient(to right, #fafaf8, transparent)",
            pointerEvents: "none",
          }}/>
          <div style={{
            position: "absolute", right: 0, top: 0, bottom: 0, width: 80, zIndex: 2,
            background: "linear-gradient(to left, #fafaf8, transparent)",
            pointerEvents: "none",
          }}/>

          <style>{`
            @keyframes sp-marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
            .sp-marquee-inner { animation: sp-marquee 28s linear infinite; display:flex; width:max-content; }
            .sp-marquee-inner:hover { animation-play-state: paused; }
          `}</style>

          <div className="sp-marquee-inner">
            {[...Array(2)].map((_, rep) => (
              <React.Fragment key={rep}>
                {[
                  { emoji:"🍽️", label:"Café Bliss, Pune"         },
                  { emoji:"🏨", label:"The Raj Hotel, Jaipur"    },
                  { emoji:"💇", label:"Glamour Salon, Delhi"      },
                  { emoji:"💆", label:"Serenity Spa, Goa"         },
                  { emoji:"🍕", label:"Pizzeria Milano, Mumbai"   },
                  { emoji:"👩‍🍳", label:"Pooja's Kitchen, Mumbai"  },
                  { emoji:"🌿", label:"FreshBowl, Bengaluru"      },
                  { emoji:"🏩", label:"Boutique Inn, Udaipur"     },
                ].map(({ emoji, label }) => (
                  <div key={`${rep}-${label}`} style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "9px 20px", margin: "0 6px",
                    background: "#fff", border: "1.5px solid #efefea",
                    borderRadius: 999, whiteSpace: "nowrap" as const,
                    ...T.zb, fontSize: 12, fontWeight: 500, color: "#666",
                    boxShadow: "0 1px 6px rgba(0,0,0,.04)",
                  }}>
                    <span style={{ fontSize: 14 }}>{emoji}</span>
                    {label}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div style={{
          marginTop: 44, display: "flex", justifyContent: "center",
          gap: 10, flexWrap: "wrap" as const,
        }}>
          {[
            { e:"⭐", t:"Rated 5.0 by early customers"      },
            { e:"🇮🇳", t:"Built for Indian service businesses" },
            { e:"🚀", t:"New businesses onboarding daily"    },
          ].map(({ e, t }) => (
            <span key={t} className="sp-pill-hover" style={{
              ...T.zb,
              display: "inline-flex", alignItems: "center", gap: 7,
              background: "#fff", border: "1.5px solid #efefea",
              borderRadius: 999, padding: "8px 18px",
              fontSize: 12, fontWeight: 500, color: "#666",
              boxShadow: "0 1px 6px rgba(0,0,0,.04)",
            }}>
              <span style={{ fontSize: 14 }}>{e}</span>{t}
            </span>
          ))}
        </div>

      </div>
    </section>
  );
};

export default SocialProof;